<script setup lang="ts">
import { onMounted, defineEmits } from "vue";

import "@maersk-global/mds-components-core-tag";
import "@maersk-global/mds-components-core-tooltip";
import "@maersk-global/mds-components-core-icon";

import CommonDateTime from "@/components/common/CommonDateTime.vue";
import type { PaginationAttributes } from "@/interface/pagination_interface";
import type { ExpectedDate } from "@/interface/common/table_column_interface";
import type { EquipmentDetails } from "@/interface/import/import_asset_interface";
import { useImportAssetStore } from "@/store/import/import_asset_store";
import { useImportLocationStore } from "@/store/import/import_location_store";
import { useGlobalStore } from "@/store/global";

import HoldsExplanation from "../holdsExplanation/HoldsExplanation_PE018U.vue";
import HoldsExplanationTable from "../holdsExplanation/HoldsExplanation_IN103L.vue";

import { appButtonClick } from "@/utils/analytics";

const globalStore = useGlobalStore();
const locationStore = useImportLocationStore();

const emit = defineEmits(["pagechange"]);

const labels = {
  yes: globalStore.sitecoreData.common.yes,
  no: globalStore.sitecoreData.common.no,
  noResult: globalStore.sitecoreData.search.not_found,
  vesselDisclaimer: globalStore.sitecoreData.container_validation.schedule_disclaimer
};
// Use the store
const importAssetsStore = useImportAssetStore();

//Disable row checkbox for rows with status 'On schedule'.
const shouldDisableRowSelection = (row) => {
  return row.isAlreadyAdded;
};

onMounted((): void => {
  const mcTable = document.querySelector("mc-table");

  if (mcTable && mcTable !== null) {
    const hightLightRowNumberForReady = getCustomHighLightRowNumber("isReadyForDelivery");
    const hightLightRowNumberForHolds = getCustomHighLightRowNumber("otherHolds");
    const hightLightRowNumberForCombineHolds = getCustomHighLightRowNumber("combinedHolds");
    const hightLightRowNumberForFreightHold = getCustomHighLightRowNumber("freightHold");
    const hightLightRowNumberForHasAppointment = getCustomHighLightRowNumber("hasAppointment");

    const rowStyles = `
      .mds-table tr td:nth-child(${hightLightRowNumberForReady}) {
        background-color: #f7f4e7 !important;
      }
      .mds-table tr td:nth-child(${hightLightRowNumberForHolds}) {
        background-color: #f7f4e7 !important;
      }
      .mds-table tr td:nth-child(${hightLightRowNumberForCombineHolds}) {
        background-color: #f7f4e7 !important;
      }
      .mds-table tr td:nth-child(${hightLightRowNumberForFreightHold}) {
        background-color: #f7f4e7 !important;
      }
      .mds-table tr td:nth-child(${hightLightRowNumberForHasAppointment}) {
        background-color: #f7f4e7 !important;
      }
    `;

    mcTable.customstyles = rowStyles;
    mcTable.selectrowdisabled = shouldDisableRowSelection;
  }
});

const getCustomHighLightRowNumber = (columnName: string) => {
  const position = props.columnNames.findIndex((col) => col.id === columnName);
  return position === -1 ? 0 : position + 2; // +1 because nth-child is 1-based index
};

const props = defineProps({
  assets: {
    type: Array as () => EquipmentDetails[],
    required: true
  },
  currentPage: {
    type: Number,
    required: true
  },
  pagination: {
    type: Object as () => PaginationAttributes,
    required: true
  },
  columnNames: {
    type: Array as () => any[],
    required: true
  },
  totalpages: {
    type: Number,
    required: true
  },
  terminal: {
    type: String,
    required: false,
    default: ""
  }
});

// Get if the value is expected date or actual date
const getDateTime = (row: EquipmentDetails): ExpectedDate => {
  const actualDate = row.actualTimeArrivalDateTimeLocal != null && row.actualTimeArrivalDateTimeLocal !== "";
  if (actualDate) {
    return {
      isActualDate: actualDate,
      expectedDate: row.actualTimeArrivalDateTimeLocal
    };
  } else {
    return {
      isActualDate: actualDate,
      expectedDate: row.expectedTimeArrivalDateTimeLocal
    };
  }
};

// Container selected for the watchlist
const containerSelected = (event: any): void => {
  const container = event.detail as EquipmentDetails[];
  importAssetsStore.addContainerToSelectedContainerList(container);
};

const handleSortChange = (event: any) => {
  appButtonClick("Import Availability", "Sort Button Click", props.terminal, {
    ...locationStore.anayticsTerminalDetails,
    sortColumnName: event.detail.column,
    sortDirection: event.detail.direction
  });
};

const isBlankObject = (value: any) => {
  return typeof value === "object" && value !== null && Object.keys(value).length === 0 ? "" : value;
};

const paginationChangeHandler = (event) => {
  emit("pagechange", event);
};

const beautifyBOLs = (bols: string): string => {
  if (!bols) return "";

  if (typeof bols === "string" && bols.includes(",")) {
    return bols
      .split(",")
      .map((item) => item.trim())
      .join(" ");
  }

  return bols;
};
</script>

<template>
  <div class="my-table-container">
    <mc-table
      :data.prop="props.assets"
      :columns.prop="props.columnNames"
      :recordsperpage="pagination.pageSize"
      :currentpage="currentPage"
      select
      fit="small"
      :selectrowdisabled="props.assets"
      sortdefaultcolumnid=""
      zebraStripes
      @sortchange="handleSortChange($event)"
      @selectchange="containerSelected($event)"
    >
      <div v-if="!assets.length" slot="state">{{ labels.noResult }}</div>
      <template v-for="row in assets" :key="row.id">
        <!-- Container Number -->
        <div :slot="`${row.id}_containerNumber`">
          <div class="sub-heading">{{ row.containerNumber }}</div>
        </div>

        <div :slot="`${row.id}_billOfLadingNumber`">
          <div class="sub-heading">{{ beautifyBOLs(row.billOfLadingNumber) }}</div>
        </div>

        <div :slot="`${row.id}_isReadyForDelivery`" style="display: flex; justify-content: center">
          <div v-if="locationStore.anayticsTerminalDetails.oneStreamCode === 'SE031L'">
            <div v-if="row.isReadyForDelivery">
              <mc-icon slot="trigger" icon="check-circle" color="#6CA926" size="20" class="success"></mc-icon>
            </div>
            <div v-else>
              <div v-if="row.gatedOutDateTimeLocal && row.gatedOutDateTimeLocal !== ''">
                <mc-tooltip fit="small" :opendelay="50" :zindex="99999" position="top-center">
                  <mc-icon slot="trigger" icon="exclamation-circle" color="#fcb75b" size="20" class="warning"></mc-icon>
                  <span class="table-small-font">{{
                    globalStore.sitecoreData.terminal_specific["SE031L"].container_picked_up
                  }}</span>
                </mc-tooltip>
              </div>
              <div v-else>
                <mc-tooltip fit="small" :opendelay="50" :zindex="99999" position="top-center">
                  <mc-icon slot="trigger" icon="times-circle" color="#bc504f" size="20" class="error"></mc-icon>
                  <span class="table-small-font">{{
                    globalStore.sitecoreData.terminal_specific["SE031L"].container_not_in_yard
                  }}</span></mc-tooltip
                >
              </div>
            </div>
          </div>
          <div v-else>
            <div v-if="row.readyForDeliveryReason === ''">
              <mc-tag fit="small" :appearance="row.isReadyForDelivery ? 'success' : 'error'"
                >{{ row.isReadyForDelivery ? labels.yes : labels.no }}
              </mc-tag>
            </div>
            <div v-else>
              <mc-tooltip
                fit="small"
                :opendelay="50"
                appearance="neutral-default"
                :zindex="99999"
                position="top-center"
              >
                <mc-tag slot="trigger" fit="small" :appearance="row.isReadyForDelivery ? 'success' : 'error'"
                  >{{ row.isReadyForDelivery ? labels.yes : labels.no }}
                </mc-tag>
                <span class="table-small-font">{{ row.readyForDeliveryReason }}</span>
              </mc-tooltip>
            </div>
          </div>
        </div>

        <!-- Yard Position -->
        <div :slot="`${row.id}_vesselOperator`">
          <div class="heading">
            {{ row.vesselOperator }}
          </div>
        </div>

        <!-- Vessel Name -->
        <div :slot="`${row.id}_vesselName`">{{ row.vesselName }}</div>

        <!-- Vessel ETA -->
        <CommonDateTime
          :id="`${row.id}_vesselEta`"
          :icon="'container-right'"
          :actual="row.expectedTimeArrivalDateTimeLocal"
          :estimated="row.expectedTimeArrivalDateTimeLocal"
        />

        <!-- Vessel Shipping Line Operator -->
        <div :slot="`${row.id}_shippingLineOperator`">{{ row.shippingLine || row.vesselOperator }}</div>

        <!-- Yard Position -->
        <div :slot="`${row.id}_yardPosition`">{{ row.yardPosition }}</div>

        <!-- Scheduled departure time -->
        <div
          :slot="`${row.id}_dischargeDateTimeLocal`"
          :style="`padding-left: ${props.terminal === 'NL071E' ? '30px' : '0px'}`"
        >
          <div v-if="props.terminal === 'NL071E'">
            <mc-tag
              fit="small"
              :appearance="!row.dischargeDateTimeLocal || row.dischargeDateTimeLocal === '' ? 'error' : 'success'"
              >{{ !row.dischargeDateTimeLocal || row.dischargeDateTimeLocal === "" ? labels.no : labels.yes }}
            </mc-tag>
          </div>
          <div v-else>
            <CommonDateTime
              :id="`${row.id}_dischargeDateTimeLocal`"
              :icon="'container-right'"
              :actual="row.dischargeDateTimeLocal"
              :estimated="row.dischargeDateTimeLocal"
            />
          </div>
        </div>

        <!-- Container status -->
        <div :slot="`${row.id}_containerState`">
          {{ row.containerState }}
        </div>

        <div :slot="`${row.id}_otherHolds`" style="width: 150px; word-wrap: break-word">
          <div v-if="row.otherHoldsDescription === ''">{{ isBlankObject(row.otherHolds) }}</div>
          <div v-else>
            <mc-tooltip fit="small" :opendelay="50" appearance="neutral-default" :zindex="99999" position="top-center">
              <span slot="trigger">{{ isBlankObject(row.otherHolds) }}</span>
              <span class="table-small-font">{{ row.otherHoldsDescription }}</span>
            </mc-tooltip>
          </div>
        </div>

        <div :slot="`${row.id}_combinedHolds`">
          <div v-if="row.otherHoldsDescription === ''">{{ isBlankObject(row.combinedHolds) }}</div>
          <div v-else>
            <mc-tooltip fit="small" :opendelay="50" appearance="neutral-default" :zindex="99999" position="top-center">
              <span slot="trigger">{{ isBlankObject(row.combinedHolds) }}</span>
              <span class="table-small-font">{{ row.otherHoldsDescription }}</span>
            </mc-tooltip>
          </div>
        </div>

        <div :slot="`${row.id}_freightHold`">
          {{ isBlankObject(row.freightHold) }}
        </div>

        <div :slot="`${row.id}_customsHold`">
          {{ isBlankObject(row.customsHold) }}
        </div>

        <!-- Container Net Weight -->
        <div :slot="`${row.id}_netWeight`">{{ row.netWeight }}</div>

        <!-- Container Gross Weight -->
        <div :slot="`${row.id}_grossWeight`">{{ row.grossWeight }}</div>

        <!-- Container HazardousClass -->
        <div :slot="`${row.id}_hazardousClass`">{{ row.hazardousClass }}</div>

        <!-- Appoitment -->
        <div :slot="`${row.id}_hasAppointment`" style="display: flex; justify-content: center">
          {{ row.hasAppointment ? labels.yes : labels.no }}
        </div>

        <!-- Estimated or Actual arrival time -->
        <CommonDateTime
          :id="`${row.id}_appointmentDateTimeLocal`"
          :icon="'vessel-side'"
          :actual="row.roadGateAppointmentDateTimeLocal"
          :estimated="row.roadGateAppointmentDateTimeLocal"
          :showNo="true"
        />

        <!-- Good Through Date Time -->
        <CommonDateTime
          :id="`${row.id}_goodThroughDateTimeLocal`"
          :icon="'vessel-side'"
          :actual="row.goodThroughDateTimeLocal"
          :estimated="row.goodThroughDateTimeLocal"
          :hideTime="true"
        />

        <!-- Actual Time Arrival Date Time Local -->
        <CommonDateTime
          :id="`${row.id}_actualTimeArrivalDateTimeLocal`"
          :showETA="true"
          :icon="'vessel-side'"
          :actual="getDateTime(row).expectedDate"
          :estimated="row.actualTimeArrivalDateTimeLocal"
        />

        <div :slot="`${row.id}_demurrageOwed`">
          {{ row.demurrageOwed }}
        </div>

        <!-- Container Strip Date Time Local -->
        <CommonDateTime
          :id="`${row.id}_containerStripDateTimeLocal`"
          :icon="'vessel-side'"
          :actual="row.containerStripDateTimeLocal"
          :estimated="row.containerStripDateTimeLocal"
        />

        <!-- Container ISO and Equipment Height -->
        <div :id="`${row.id}_containerSizeTypeHeight`" class="vessel-inbounds">
          <span class="color-text">{{ row.containerSizeTypeHeight }}</span>
          <span> | </span>
          <span class="color-text">{{ row.equipmentTypeIsoCode }}</span>
        </div>

        <!-- Scheduled departure time -->
        <CommonDateTime
          :id="`${row.id}_gatedOutDateTimeLocal`"
          :icon="'calendar-clock'"
          :actual="row.gatedOutDateTimeLocal"
          :estimated="row.gatedOutDateTimeLocal"
        />
        <div :slot="`${row.id}_customsStatus`">
          {{ row.customsStatus || "" }}
        </div>
        <div :slot="`${row.id}_movementReferenceNumber`" style="width: 180px">
          {{ row.movementReferenceNumber || "" }}
        </div>
      </template>
    </mc-table>
  </div>
  <div v-if="totalpages > 1" class="my-table-pagination">
    <mc-pagination
      fit="small"
      :totalpages="props.totalpages"
      previouslabel=""
      nextlabel=""
      @pagechange="paginationChangeHandler"
    />
  </div>

  <div class="spacer"></div>
  <div v-if="locationStore.anayticsTerminalDetails.oneStreamCode === 'PE018U'">
    <HoldsExplanation journey="import" terminalOneStreamCode="PE018U" />
  </div>
  <div v-if="locationStore.anayticsTerminalDetails.oneStreamCode === 'IN103L'">
    <HoldsExplanationTable journey="import" terminalOneStreamCode="IN103L" />
  </div>
  <div class="spacer"></div>
  <div class="discliamer_section">{{ labels.vesselDisclaimer }}</div>
</template>

<style lang="scss" scoped>
mc-icon::part(icon) {
  stroke-width: 1.2;
  paint-order: stroke fill;
  stroke-linejoin: round;
  stroke-linecap: round;
}
.error {
  stroke: #bc504f;
}
.warning {
  stroke: #fcb75b;
}
.success {
  stroke: #6ca926;
}
mc-icon {
  width: 32px;
  height: 32px;
}
</style>
